import $ from 'jquery';
import debounce from 'lodash/debounce';
import pickBy from 'lodash/pickBy';
import findLastKey from 'lodash/findLastKey';
import PubSub from 'pubsub-js';

export default class MediaQueryHelper {

  constructor(config = {}) {

    this.config = {
      watch: false,
      debounce: 250,
      ...config,
    };

    this.queries = {};
    this.current = '';

    // Seup watch handler
    this.setupWatchHandler();

    // Add default queries
    this.addQueries({
      landscape: 'only screen and (orientation: landscape)',
      portrait: 'only screen and (orientation: portrait)',
      dark: 'only screen and (prefers-color-scheme: dark)',
    });

    // Add user queries
    this.addQueries(config.queries);
  }

  /**
   * Setup and debounce watch handler
   *
   */

  setupWatchHandler() {

    // Default: just proxy events through to handler
    this.watchHandler = this.handleMatchChange;

    // Debounce?
    if (this.config.watch && this.config.debounce > 0) {
      this.isResizing = false;
      this.watchHandlerArgs = [];

      this.watchHandler = (...args) => {
        this.watchHandlerArgs = [...args];
        if (this.isResizing) return;
        this.handleMatchChange(...args);
      };

      $(window).on('resize', debounce(() => {
        this.isResizing = true;
      }, this.config.debounce, { leading: true, trailing: false }));

      $(window).on('resize', debounce(() => {
        this.isResizing = false;
        this.watchHandler(...this.watchHandlerArgs);
      }, this.config.debounce));
    }
  }

  /**
   * Get a query by name (or return dummy object)
   *
   */

  getQuery(name) {
    return this.queries[name] || {
      matches: false,
      addListener() {},
      removeListener() {},
    };
  }

  /**
   * Get all queries that match by size (min-width)
   *
   */

  isSizeQuery(query) {
    return query.media.indexOf('min-width') !== -1;
  }

  getSizeQueries() {
    return pickBy(this.queries, this.isSizeQuery);
  }

  /**
   * Add query, transform size queries and add listener
   *
   */

  addQuery(name, query) {
    if (!name || !query) return;

    // Transform number-only values into min-width queries
    let value = query;
    if (/^\d/.test(query)) {
      value = `only screen and (min-width: ${query})`;
    }

    // Create matchMedia object
    this.queries[name] = window.matchMedia(value);

    // Add event listener
    this.watchQuery(name);
  }

  /**
   * Add an object of multiple queries at once
   *
   */

  addQueries(queries) {
    if (typeof queries === 'object') {
      Object.keys(queries).forEach(name => this.addQuery(name, queries[name]));
    }
  }

  /**
   * Attach change listener to query
   *
   */

  watchQuery(name) {
    if (!this.config.watch) return;
    this.getQuery(name).addListener(this.createListener(name));
  }

  /**
   * Create event listener with query name in function scope
   *
   */

  createListener(name) {
    return (mm) => {
      this.watchHandler(name, mm);
    };
  }

  /**
   * Change listener
   *
   */

  handleMatchChange(name, mm) {
    this.current = this.getCurrentSize();
    PubSub.publish('mediaquery.change', this);
    if (mm && mm.matches) {
      PubSub.publish(`mediaquery.${name}`, mm.matches);
    }
  }

  /**
   * Calculate current size by finding last matching size query
   *
   */

  getCurrentSize() {
    return findLastKey(this.getSizeQueries(), query => query.matches);
  }

  /**
   * Check if query matches
   *
   */

  is(name) {
    return this.getQuery(name).matches;
  }
}
